import React, { useState } from "react";
import { submitLead } from "../lib/api";
import { Checkbox } from "../components/ui/checkbox";
import { CheckCircle2, Loader2, ArrowRight, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function EmailForm() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const [company, setCompany] = useState(""); // honeypot

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (company) {
      setSuccess({ mocked: false });
      return;
    }
    if (!email.trim()) return setError("Bitte gib deine E-Mail ein.");
    if (!consent) return setError("Bitte bestätige den Datenschutz.");

    setLoading(true);
    try {
      const data = await submitLead(email.trim(), consent);
      setSuccess({
        already: data.already_registered,
        mocked: data.email_mocked,
        sent: data.email_sent,
      });
      toast.success(data.already_registered ? "Du bist schon eingetragen." : "Check deine Mails!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Etwas ist schief gelaufen. Bitte nochmal versuchen.";
      setError(typeof msg === "string" ? msg : "Ungültige Eingabe.");
      toast.error("Eintrag fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="success-state"
        className="w-full max-w-2xl mx-auto border border-[#E8B14A]/30 bg-[#E8B14A]/5 rounded-sm p-8 sm:p-10 text-center"
      >
        <CheckCircle2 className="w-12 h-12 mx-auto text-[#E8B14A]" strokeWidth={1.2} />
        <h3 className="mt-4 font-editorial text-4xl sm:text-5xl text-white leading-tight">
          Dein Gutschein<br />ist unterwegs.
        </h3>
        <p className="mt-4 text-zinc-300 text-base sm:text-lg max-w-md mx-auto">
          {success.already
            ? "Du bist bereits registriert. Schau in dein Postfach - dort findest du deinen Gutschein für die gratis Portion Fritten."
            : success.sent
            ? "Prüfe dein Postfach. Dein Gutschein für eine gratis Portion Fritten bei BUDDIEZ buns & rolls wartet dort auf dich."
            : "Dein Eintrag ist gespeichert. Dein Gutschein folgt in Kürze per Mail."}
        </p>

        {success.mocked && (
          <p className="mt-5 text-xs text-zinc-500 font-mono-accent uppercase tracking-widest">
            [Test-Modus] E-Mail-Versand aktuell gemockt. Lead wurde gespeichert.
          </p>
        )}

        <div className="mt-8 pt-6 border-t border-zinc-800 text-sm">
          <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-zinc-500">Einlöseort</div>
          <div className="mt-2 flex items-center justify-center gap-2 text-white font-semibold">
            <MapPin className="w-4 h-4 text-[#E0473C]" />
            <span>BUDDIEZ buns &amp; rolls · Werderstr. 79 · 70190 Stuttgart</span>
          </div>
          <div className="mt-1 text-xs text-zinc-500">Gutschein bei deinem Besuch vorzeigen · 1x pro Person einlösbar</div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="email-form"
      className="w-full max-w-2xl mx-auto"
      aria-label="Newsletter Anmeldung für Gratis-Fritten Gutschein"
    >
      {/* Honeypot */}
      <input
        type="text"
        name="company"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />

      <div className="flex flex-col sm:flex-row gap-0 sm:gap-0 border border-zinc-800 focus-within:border-[#E8B14A] rounded-sm bg-[#0f0f12] overflow-hidden transition-colors">
        <input
          id="email"
          data-testid="email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="deine@email.de"
          className="flex-1 h-16 px-5 bg-transparent text-white placeholder:text-zinc-600 focus:outline-none text-lg"
          autoComplete="email"
          required
          aria-label="E-Mail-Adresse"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="submit-button"
          className="btn-primary h-16 px-8 flex items-center justify-center gap-2 text-base disabled:opacity-60 disabled:cursor-not-allowed"
          aria-label="Gratis Fritten Gutschein sichern"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              GRATIS FRITTEN SICHERN
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>

      <div className="mt-4 flex items-start gap-3">
        <Checkbox
          id="consent"
          data-testid="consent-checkbox"
          checked={consent}
          onCheckedChange={(v) => setConsent(Boolean(v))}
          className="mt-0.5 border-zinc-600 data-[state=checked]:bg-[#E8B14A] data-[state=checked]:border-[#E8B14A] data-[state=checked]:text-black"
        />
        <label htmlFor="consent" className="text-xs text-zinc-400 leading-relaxed select-none">
          Ich willige ein, dass meine E-Mail für den Versand des Gutscheins und gelegentliche Updates
          von bestburgerstuttgart.de gespeichert wird. Details in der{" "}
          <Link to="/datenschutz" className="underline hover:text-[#E8B14A]" data-testid="privacy-link">
            Datenschutzerklärung
          </Link>
          . Abmeldung jederzeit per Klick.
        </label>
      </div>

      {error && (
        <p data-testid="form-error" className="mt-3 text-sm text-[#E0473C] font-medium">
          {error}
        </p>
      )}
    </form>
  );
}
